import React, { useState, useRef, useEffect } from 'react';
import { 
  Camera, 
  Heart, 
  MessageCircle, 
  Send,
  X,
  Flag,
  ThumbsDown,
  Trash2
} from 'lucide-react';
import { NoAutoFillInput, NoAutoFillTextarea } from './ui/NoAutoFillInput';

const API_BASE_URL = window.__api_base_url || (process.env.NODE_ENV === 'production' ? 'https://openyc-api-backend.fly.dev' : 'http://localhost:8081');

const Moments = ({ isOpen, onClose, userId, userName, userAvatar }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostImages, setNewPostImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showComments, setShowComments] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      fetchMoments();
    }
  }, [isOpen]);

  const fetchMoments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/moments`, {
        headers: {
          'x-user-id': userId || ''
        }
      });
      const data = await response.json();
      if (data.success) {
        setPosts(data.moments);
      }
    } catch (error) {
      console.error('Error fetching moments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && newPostImages.length === 0) return;
    if (!userId) {
      alert('请先登录');
      return;
    }
    if (isPosting) return;

    setIsPosting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/moments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          userName,
          content: newPostContent,
          images: newPostImages
        })
      });

      const data = await response.json();
      if (data.success) {
        setPosts([{ ...data.moment, isLiked: false, commentsList: [] }, ...posts]);
        setNewPostContent('');
        setNewPostImages([]);
        setShowCreateModal(false);
      } else {
        alert(data.error || '发布失败');
      }
    } catch (error) {
      console.error('Error creating post:', error);
      alert('发布失败，请重试');
    } finally {
      setIsPosting(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const MAX_IMAGES = 18;
    
    setNewPostImages(prev => {
      const availableSlots = MAX_IMAGES - prev.length;
      if (availableSlots <= 0) {
        alert(`最多只能添加${MAX_IMAGES}张图片`);
        return prev;
      }
      
      const filesToProcess = files.slice(0, availableSlots);
      if (files.length > availableSlots) {
        alert(`已选择${files.length}张图片，但只能添加${availableSlots}张（最多${MAX_IMAGES}张）`);
      }
      
      filesToProcess.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setNewPostImages(current => [...current, reader.result]);
        };
        reader.readAsDataURL(file);
      });
      
      return prev;
    });
  };

  const handleLike = async (postId) => {
    if (!userId) {
      alert('请先登录');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/moments/${postId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, userName })
      });

      const data = await response.json();
      if (data.success) {
        setPosts(posts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              likes: data.liked ? post.likes + 1 : post.likes - 1,
              isLiked: data.liked
            };
          }
          return post;
        }));
      }
    } catch (error) {
      console.error('Error liking moment:', error);
    }
  };

  const handleAddComment = async (postId) => {
    if (!newComment.trim()) return;
    if (!userId) {
      alert('请先登录');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/moments/${postId}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          userId, 
          userName, 
          content: newComment 
        })
      });

      const data = await response.json();
      if (data.success) {
        setPosts(posts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              comments: post.comments + 1,
              commentsList: [...(post.commentsList || []), data.comment]
            };
          }
          return post;
        }));
        setNewComment('');
      }
    } catch (error) {
      console.error('Error commenting:', error);
      alert('评论失败，请重试');
    }
  };

  const handleReport = (postId) => {
    alert('已举报该动态');
    setShowActionMenu(null);
  };

  const handleNotInterested = (postId) => {
    setPosts(posts.filter(post => post.id !== postId));
    setShowActionMenu(null);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('确定要删除这条动态吗？')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/moments/${postId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId })
      });

      const data = await response.json();
      if (data.success) {
        setPosts(posts.filter(post => post.id !== postId));
        alert('动态已删除');
      } else {
        alert(data.error || '删除失败');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('删除失败，请重试');
    }
    setShowActionMenu(null);
  };

  const formatTime = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 7) return `${days}天前`;
    return new Date(timestamp).toLocaleDateString('zh-CN');
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-[100] bg-black/50 transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleClose}
    >
      <div 
        className={`absolute inset-x-0 bottom-0 h-full bg-black rounded-t-[32px] transition-transform duration-300 ease-out ${
          isAnimating ? 'translate-y-0' : 'translate-y-full'
        }`}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-14 flex items-center justify-between px-4 border-b border-white/10">
          <button onClick={handleClose} className="p-2 -ml-2">
            <X size={24} className="text-white" />
          </button>
          <h1 className="text-lg font-semibold text-white">moments</h1>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="p-2 -mr-2"
          >
            <Camera size={24} className="text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-black h-[calc(100%-56px)]">
          <div className="flex gap-4 p-4 overflow-x-auto border-b border-white/10">
            <div className="flex flex-col items-center gap-1 min-w-[72px]">
              <div className="w-[72px] h-[72px] rounded-full border-2 border-[var(--neon-primary)] p-0.5">
                <img 
                  src={userAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userName || 'me'}`} 
                  alt="Me" 
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
              <span className="text-xs text-white">我的故事</span>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10 text-gray-400">
              <p>加载中...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <p>暂无动态，点击右上角相机发布第一条吧！</p>
            </div>
          ) : (
            posts.map(post => (
              <div key={post.id} className="border-b border-white/10 pb-4 mb-4">
                <div className="flex items-center gap-3 px-4 py-3">
                  <img 
                    src={post.avatar || post.userAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.userName || post.userId}`}
                    alt={post.displayName || post.userName}
                    className="w-8 h-8 rounded-full"
                  />
                  <span className="font-semibold text-white text-sm">{post.displayName || post.userName}</span>
                  <span className="text-gray-500 text-xs ml-auto">{formatTime(post.createdAt || post.timestamp)}</span>
                </div>

                {post.images && post.images.length > 0 && (
                  <div className={`grid gap-1 px-4 ${
                    post.images.length === 1 ? 'grid-cols-1' : 
                    post.images.length === 2 ? 'grid-cols-2' : 
                    'grid-cols-3'
                  }`}>
                    {post.images.map((img, idx) => (
                      <img 
                        key={idx}
                        src={img} 
                        alt=""
                        className="w-full aspect-square object-cover cursor-pointer"
                        onClick={() => setSelectedImage(img)}
                      />
                    ))}
                  </div>
                )}

                {post.content && (
                  <p className="px-4 py-2 text-white text-sm">{post.content}</p>
                )}

                <div className="flex items-center justify-between px-4 pt-2">
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => handleLike(post.id)}
                      className="flex items-center gap-1"
                    >
                      <Heart 
                        size={24} 
                        className={post.isLiked ? 'text-red-500 fill-red-500' : 'text-white'}
                      />
                    </button>
                    <button 
                      onClick={() => setShowComments(showComments === post.id ? null : post.id)}
                    >
                      <MessageCircle size={24} className={showComments === post.id ? 'text-[var(--neon-primary)]' : 'text-white'} />
                    </button>
                  </div>
                  <div className="relative">
                    <button onClick={() => setShowActionMenu(showActionMenu === post.id ? null : post.id)}>
                      <span className="text-white text-xl">•••</span>
                    </button>
                    
                    {showActionMenu === post.id && (
                      <div className="absolute right-0 bottom-8 w-40 bg-[#1a1a2e] rounded-xl border border-white/10 overflow-hidden shadow-xl">
                        {post.userId === userId && (
                          <button 
                            onClick={() => handleDeletePost(post.id)}
                            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors"
                          >
                            <Trash2 size={18} className="text-red-400" />
                            <span className="text-white text-sm">删除</span>
                          </button>
                        )}
                        <button 
                          onClick={() => handleReport(post.id)}
                          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors"
                        >
                          <Flag size={18} className="text-red-400" />
                          <span className="text-white text-sm">举报</span>
                        </button>
                        <button 
                          onClick={() => handleNotInterested(post.id)}
                          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors"
                        >
                          <ThumbsDown size={18} className="text-gray-400" />
                          <span className="text-white text-sm">不感兴趣</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="px-4 pt-2">
                  {post.likes > 0 && (
                    <p className="text-white text-sm font-semibold">{post.likes} 次赞</p>
                  )}
                </div>

                {showComments === post.id && (
                  <div className="px-4 pt-2 mt-2 border-t border-white/10">
                    {post.commentsList && post.commentsList.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {post.commentsList.map((comment, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <img 
                              src={comment.userAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${comment.userName}`}
                              alt={comment.userName}
                              className="w-6 h-6 rounded-full"
                            />
                            <div className="flex-1">
                              <span className="text-[var(--neon-primary)] text-xs font-semibold">{comment.userName}</span>
                              <p className="text-white text-sm">{comment.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {userId && (
                      <div className="flex gap-2">
                        <NoAutoFillInput
                          type="text"
                          inputMode="text"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="添加评论..."
                          name="moment-comment"
                          id="moment-comment"
                          className="flex-1 px-3 py-2 rounded-lg bg-white/10 text-white text-sm outline-none focus:bg-white/20"
                          onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                        />
                        <button
                          onClick={() => handleAddComment(post.id)}
                          disabled={!newComment.trim()}
                          className="px-3 py-2 rounded-lg bg-[var(--neon-primary)] text-black text-sm font-semibold disabled:opacity-50"
                        >
                          <Send size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {showCreateModal && (
          <div className="fixed inset-0 z-[110] bg-black flex flex-col">
            <div className="h-14 flex items-center justify-between px-4 border-b border-white/10">
              <button onClick={() => setShowCreateModal(false)}>
                <X size={24} className="text-white" />
              </button>
              <h2 className="text-lg font-semibold text-white">real moments</h2>
              <button 
                onClick={handleCreatePost}
                disabled={isPosting || (!newPostContent.trim() && newPostImages.length === 0)}
                className={`px-4 py-1.5 rounded-lg font-semibold text-sm transition-all ${
                  isPosting 
                    ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                    : 'bg-[var(--neon-primary)] text-black'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isPosting ? '发布中...' : '发布'}
              </button>
            </div>

            <div className="flex-1 p-4">
              <NoAutoFillTextarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="分享你的新鲜事..."
                name="moment-content"
                id="moment-content"
                className="w-full h-32 bg-transparent text-white resize-none outline-none"
              />

              {newPostImages.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-4">
                  {newPostImages.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img src={img} alt="" className="w-24 h-24 object-cover rounded-lg" />
                      <button 
                        onClick={() => setNewPostImages(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <X size={14} className="text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {newPostImages.length < 18 && (
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-4 w-full py-4 border border-dashed border-white/30 rounded-lg flex items-center justify-center gap-2"
                >
                  <Camera size={24} className="text-white/60" />
                  <span className="text-white/60">
                    添加照片 ({newPostImages.length}/18)
                  </span>
                </button>
              )}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                ref={fileInputRef}
                onChange={handleImageUpload}
              />
            </div>
          </div>
        )}

        {selectedImage && (
          <div 
            className="fixed inset-0 z-[120] bg-black flex items-center justify-center"
            onClick={() => setSelectedImage(null)}
          >
            <img src={selectedImage} alt="" className="max-w-full max-h-full object-contain" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Moments;
